import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LuCookie, LuX, LuCheck } from 'react-icons/lu';

export const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Check if user has already consented
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Show banner after a short delay
      const timer = setTimeout(() => setShowBanner(true), 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptCookies = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setShowBanner(false);
    
    // Enable analytics if needed
    if (window.posthog) {
      window.posthog.opt_in_capturing();
    }
  };

  const declineCookies = () => {
    localStorage.setItem('cookieConsent', 'declined');
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setShowBanner(false);
    
    // Disable analytics
    if (window.posthog) {
      window.posthog.opt_out_capturing();
    }
  };

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="fixed bottom-0 left-0 right-0 z-[60] p-4 md:p-6"
          data-testid="cookie-consent-banner"
        >
          <div className="container mx-auto max-w-4xl">
            <div className="bg-slate-900/98 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-4 md:p-6 shadow-2xl">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                {/* Icon & Text */}
                <div className="flex items-start gap-4 flex-1">
                  <div className="w-10 h-10 rounded-xl bg-orange-500/20 border border-orange-500/30 flex items-center justify-center flex-shrink-0">
                    <LuCookie size={20} className="text-orange-400" />
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-white text-sm mb-1">We value your privacy</h4>
                    <p className="text-slate-400 text-xs md:text-sm leading-relaxed">
                      We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. 
                      By clicking "Accept", you consent to our use of cookies.{' '}
                      <a href="/privacy" className="text-blue-400 hover:text-blue-300 underline">
                        Learn more
                      </a>
                    </p>
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex items-center gap-3 w-full md:w-auto">
                  <button
                    onClick={declineCookies}
                    className="flex-1 md:flex-none px-4 py-2.5 text-sm font-medium text-slate-400 hover:text-white border border-slate-700 hover:border-slate-600 rounded-xl transition-all flex items-center justify-center gap-2"
                    data-testid="decline-cookies"
                  >
                    <LuX size={16} />
                    <span className="hidden sm:inline">Decline</span>
                  </button>
                  <button
                    onClick={acceptCookies}
                    className="flex-1 md:flex-none px-5 py-2.5 text-sm font-bold text-white bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-500 hover:to-orange-400 rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-orange-500/20"
                    data-testid="accept-cookies"
                  >
                    <LuCheck size={16} />
                    Accept
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
